<template>
<section class="view-wrap">
	<Heading :level="1" text="Your Documents" />

	<div>
		<p class="m-b-2">As you start to add file attachments to your application, you will see them listed here and click to view them. You can also upload further documents if they are requested to support your application.</p>

		<template v-if="getAllNotes(applicationTypeID).length">
			<div class="table-wrap">
				<Table :table-data="files" />
			</div>
		</template>

		<p v-if="!getAllNotes(applicationTypeID).length && !notesLoading">
			It doesn't look like you've uploaded any documents yet. You can add them below, or in the appropriate page in the application form.
		</p>
	</div>
	<div class="m-bl-2 p-bl-2 contrast-bg">
		<form-field-set class="view-wrap three-quarters">
			<template #legend>
				<legend>Upload Additional Documents</legend>
			</template>
			<template #fields>
				<SelectField
					ref="fileType"
					name="fileType"
					label-text="Please select the type of file you are uploading"
					:value="noteSubject"
					:options="fileTypeOpts[stage]"
					@emitFieldData="(val) => noteSubject = val"
					required-error="Please select an option"
					:options-loading="false" />

				<template v-if="noteSubject !== ''">
					<FileUpload
						ref="additionalUploads"
						name="additionalUploads"
						:is-multi="true"
						@fileUploadErrors="(val) => fileUploadError = val"
						@uploadFiles="uploadFiles()"
						@emitFieldData="v => prepareFiles(v)"
						:is-required="getUploadedFiles && !getUploadedFiles.length"
						:uploaded-files="getUploadedFiles"
						:upload-msg="uploadMsg"
						label-text="Please upload your additional files"
						:help-text="{text: 'If you have been asked to provide further evidence for your application, please upload it here.'}"
						required-error="Please provide your evidence"
						:validation-rules="[rules.withinFileSizeLimit]" />
				</template>
			</template>
		</form-field-set>
	</div>

	<router-link
		:to="{ name: 'Dashboard'}">
		Back to Dashboard
	</router-link>
</section>
</template>

<script>
import { format, parseISO } from 'date-fns';

import { mapGetters, mapActions } from 'vuex';
import { fileUploads } from '@/mixins/fileUploads';
import { validationRules } from '@/utilities/validationRules';

export default {
	name: 'DocumentManagement',
	mixins: [fileUploads],
	props: {
		stage: {
			type: String,
			default: ''
		},
		applicationSize: {
			type: String,
			default: 'large',
			validator (value) {
				return ['small', 'large'].includes(value);
			}
		}
	},
	data () {
		return {
			rules: validationRules,
			noteSubject: '',
			fileTypeOpts: {
				eoi: [
					{ label: 'UTR Proof', value: 'UTR_EVIDENCE' },
					{ label: 'Other Supporting Documents', value: 'OTHER_DOCUMENTS' }
				],
				application: [
					{ label: 'Latest Accounts', value: 'LATEST_ACCOUNTS' },
					{ label: 'Bank Statement', value: 'BANK_STATEMENTS' },
					{ label: 'Other Supporting Documents', value: 'OTHER_DOCUMENTS' }
				],
				claim: [
					{ label: 'Claim Evidence', value: 'CLAIM_EVIDENCE' },
					{ label: 'Other Supporting Documents', value: 'OTHER_DOCUMENTS' }
				]
			},
			files: {
				headers: ['File Name', 'Date Uploaded', 'File Subject'],
				rows: []
			},
			showList: true,
			notesLoading: true

		};
	},
	head: {
		title: `Your documents | ${process.env.VUE_APP_FUND_NAME}`,
		meta: [
			{ name: 'description', content: `Your documents for your ${process.env.VUE_APP_FUND_NAME} application` }
		]
	},
	computed: {
		...mapGetters('eoi', ['getEOIId']),
		...mapGetters('application', ['getApplicationId']),
		...mapGetters('claim', ['getCurrentClaim']),
		...mapGetters('notes', ['getAllNotes'])
	},
	created () {
		if (this.stage === 'eoi' && !this.getEOIId) {
			this.$router.push({ name: 'Dashboard' });
		} else if (this.stage === 'application' && !this.getApplicationId(this.applicationSize)) {
			this.$router.push({ name: 'Dashboard' });
		} else {
			this.populateList();
		}
	},
	methods: {
		getSubjectName (subjectID) {
			const subjects = {
				LATEST_ACCOUNTS: 'Latest Accounts',
				BANK_STATEMENTS: 'Bank Statement',
				UTR_EVIDENCE: 'UTR Evidence',
				CLAIM_EVIDENCE: 'Claim Evidence',
				OTHER_DOCUMENTS: 'Other'
			};
			return subjects[subjectID] || 'Other';
		},
		populateList () {
			this.files.rows = [];
			if (this.getAllNotes(this.applicationTypeID).length) {
				this.getAllNotes(this.applicationTypeID).forEach((item) => {
					const dateUploaded = item.createdon ? format(parseISO(item.createdon), 'dd/MM/yyyy HH:mm') : 'Unknown';

					this.files.rows.push({ cells: [item.filename, dateUploaded, this.getSubjectName(item.subject)] });
				});
			}

			this.notesLoading = false;
		},
		...mapActions('eoi', ['getEOI'])
		// ...mapActions('application', ['getApplication'])
	}
};
</script>
